"use client";

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { BookOpen, Info, LineChart, PiggyBank, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CalculatorTemplate } from "@/components/calculator/CalculatorTemplate";
import { InputForm } from "@/components/calculator/InputForm";
import { ResultCard } from "@/components/calculator/ResultCard";
import { AreaChartWrapper, PieChartWrapper } from "@/components/calculator/ChartWrapperLazy";
import { ExplanationTable } from "@/components/calculator/ExplanationTable";
import { ShareButtons } from "@/components/calculator/ShareButtons";
import { formatCurrency } from "@/lib/utils/currency";
import { calculateRetirement } from "@/lib/calculators/retirement";

const SAVINGS_MAX = 999_999_999_999;
const CONTRIBUTION_MAX = 999_999_999;

const schema = z
  .object({
    currentAge: z.coerce.number().min(18, "Min 18").max(90, "Max 90"),
    retirementAge: z.coerce.number().min(30, "Min 30").max(100, "Max 100"),
    currentSavings: z.coerce.number().min(0, "Must be 0 or more").max(SAVINGS_MAX),
    monthlyContribution: z.coerce.number().min(0, "Must be 0 or more").max(CONTRIBUTION_MAX),
    expectedReturn: z.coerce.number().min(0, "Enter valid rate").max(20),
    inflationRate: z.coerce.number().min(0, "Must be 0 or more").max(15),
  })
  .refine((data) => data.retirementAge > data.currentAge, {
    message: "Retirement age must be after current age",
    path: ["retirementAge"],
  });

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  currentAge: 35,
  retirementAge: 65,
  currentSavings: 50000,
  monthlyContribution: 500,
  expectedReturn: 7,
  inflationRate: 3,
};

const RETIREMENT_AGE_PRESETS: {
  label: string;
  values: Partial<FormValues>;
  tooltip: string;
}[] = [
  {
    label: "55 (FIRE)",
    values: { retirementAge: 55 },
    tooltip:
      "Financial Independence, Retire Early. Rule of 55 allows penalty-free 401(k) withdrawals from your current employer's plan.",
  },
  {
    label: "62 (Early SS)",
    values: { retirementAge: 62 },
    tooltip:
      "Earliest age to claim Social Security. Benefits are reduced if you claim before your full retirement age.",
  },
  {
    label: "67 (FRA)",
    values: { retirementAge: 67 },
    tooltip:
      "Full retirement age for most people born 1960 or later. Standard Social Security benefit with no reduction.",
  },
  {
    label: "70 (Max SS)",
    values: { retirementAge: 70 },
    tooltip:
      "Maximum Social Security benefits. Delayed retirement credits stop after 70.",
  },
];

const RETURN_PRESETS: { label: string; values: Partial<FormValues> }[] = [
  { label: "Conservative", values: { expectedReturn: 5, inflationRate: 3 } },
  { label: "Moderate", values: { expectedReturn: 7, inflationRate: 3 } },
  { label: "Aggressive", values: { expectedReturn: 10, inflationRate: 3 } },
];

const CHART_COLOR = "#2563eb";
const PIE_COLORS = { savings: "#64748b", contributions: "#0ea5e9", growth: "#2563eb" };

function parseValuesFromSearchParams(
  params: URLSearchParams
): Partial<FormValues> | null {
  const currentAge = params.get("currentAge");
  const retirementAge = params.get("retirementAge");
  const currentSavings = params.get("currentSavings");
  if (!currentAge || !retirementAge || !currentSavings) return null;
  const vals: Partial<FormValues> = {
    currentAge: parseInt(currentAge, 10),
    retirementAge: parseInt(retirementAge, 10),
    currentSavings: parseFloat(currentSavings),
  };
  const monthlyContribution = params.get("monthlyContribution");
  if (monthlyContribution != null) vals.monthlyContribution = parseFloat(monthlyContribution);
  const expectedReturn = params.get("expectedReturn");
  if (expectedReturn != null) vals.expectedReturn = parseFloat(expectedReturn);
  const inflationRate = params.get("inflationRate");
  if (inflationRate != null) vals.inflationRate = parseFloat(inflationRate);
  return vals;
}

function buildShareUrl(values: FormValues): string {
  const params = new URLSearchParams();
  params.set("currentAge", String(values.currentAge));
  params.set("retirementAge", String(values.retirementAge));
  params.set("currentSavings", String(values.currentSavings));
  params.set("monthlyContribution", String(values.monthlyContribution));
  params.set("expectedReturn", String(values.expectedReturn));
  if (values.inflationRate > 0)
    params.set("inflationRate", String(values.inflationRate));
  if (typeof window !== "undefined") {
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }
  return "";
}

function getFRAFromBirthYear(birthYear: number): string {
  if (birthYear >= 1960) return "67";
  if (birthYear >= 1959) return "66 and 10 months";
  if (birthYear >= 1958) return "66 and 8 months";
  if (birthYear >= 1957) return "66 and 6 months";
  if (birthYear >= 1956) return "66 and 4 months";
  if (birthYear >= 1955) return "66 and 2 months";
  if (birthYear >= 1943) return "66";
  return "65–66 (varies)";
}

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export function RetirementCalculator() {
  const mounted = useMounted();
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const [resetKey, setResetKey] = useState(0);
  const [result, setResult] = useState(() =>
    calculateRetirement({ ...defaultValues })
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const parsed = parseValuesFromSearchParams(params);
    if (parsed) {
      const merged = { ...defaultValues, ...parsed } as FormValues;
      const parsedResult = schema.safeParse(merged);
      if (parsedResult.success) {
        setFormValues(merged);
        setResult(calculateRetirement(merged));
        setResetKey((k) => k + 1);
      }
    }
  }, []);

  const handlePreset = useCallback((preset: Partial<FormValues>) => {
    const merged = { ...defaultValues, ...formValues, ...preset } as FormValues;
    const parsed = schema.safeParse(merged);
    if (parsed.success) {
      setFormValues(parsed.data);
      setResult(calculateRetirement(parsed.data));
      setResetKey((k) => k + 1);
    }
  }, [formValues]);

  const handleReset = useCallback(() => {
    setFormValues(defaultValues);
    setResult(calculateRetirement({ ...defaultValues }));
    setResetKey((k) => k + 1);
  }, []);

  const handleChange = useCallback((values: FormValues) => {
    setFormValues(values);
    setResult(calculateRetirement({ ...values }));
  }, []);

  const chartData = result.chartData.map((d) => ({
    label: `Age ${d.age}`,
    balance: Number.isFinite(d.balance) ? d.balance : 0,
  }));

  const birthYear = new Date().getFullYear() - formValues.currentAge;
  const fra = getFRAFromBirthYear(birthYear);
  const MILESTONE_AGES = [55, 62, 67, 70] as const;
  const milestones = MILESTONE_AGES.filter((age) => age > formValues.currentAge).map(
    (age) => {
      const r = calculateRetirement({ ...formValues, retirementAge: age });
      return { age, balance: r.balanceAtRetirement, monthly: r.monthlyWithdrawal };
    }
  );

  return (
    <CalculatorTemplate
      inputsSection={
        <div className="space-y-4">
          <InputForm
            key={resetKey}
            schema={schema}
            defaultValues={formValues}
            fields={[
              {
                name: "currentAge",
                label: "Current age",
                tooltip: "Your age today.",
                suffix: " years",
                min: 18,
                max: 90,
                step: 1,
                inputType: "slider-number",
              },
              {
                name: "retirementAge",
                label: "Retirement age",
                tooltip:
                  "When you plan to retire. 55 = Rule of 55 / FIRE. 62 = earliest Social Security. 67 = full retirement age for most. 70 = max SS benefits.",
                suffix: " years",
                min: 30,
                max: 100,
                step: 1,
                inputType: "slider-number",
              },
              {
                name: "currentSavings",
                label: "Current retirement savings",
                tooltip: "Total retirement savings you have now.",
                suffix: " USD",
                min: 0,
                max: SAVINGS_MAX,
                step: 1000,
                inputMode: "decimal",
              },
              {
                name: "monthlyContribution",
                label: "Monthly contribution",
                tooltip: "Amount you plan to add each month to retirement savings.",
                suffix: " USD",
                min: 0,
                max: CONTRIBUTION_MAX,
                step: 50,
                inputMode: "decimal",
                placeholder: "0",
                hideSpinner: true,
              },
              {
                name: "expectedReturn",
                label: "Expected annual return",
                tooltip: "Expected annual investment return (e.g. 7% for a diversified portfolio).",
                suffix: "%",
                min: 0,
                max: 20,
                step: 0.5,
                inputType: "slider-number",
                inputMode: "decimal",
              },
              {
                name: "inflationRate",
                label: "Expected inflation",
                tooltip: "Expected inflation for purchasing power estimates.",
                suffix: "%",
                min: 0,
                max: 15,
                step: 0.5,
                inputType: "slider-number",
                inputMode: "decimal",
                placeholder: "0",
              },
            ]}
            onChange={handleChange}
          />
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Retirement age:</span>
              {RETIREMENT_AGE_PRESETS.map((p) => {
                const isSelected = formValues.retirementAge === p.values.retirementAge;
                return (
                <Tooltip key={p.label}>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePreset(p.values)}
                        className="text-xs"
                      >
                        {p.label}
                      </Button>
                    }
                  />
                  <TooltipContent side="top" className="max-w-[260px]">
                    {p.tooltip}
                  </TooltipContent>
                </Tooltip>
              );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Return:</span>
              {RETURN_PRESETS.map((p) => {
                const isSelected =
                  formValues.expectedReturn === p.values.expectedReturn &&
                  formValues.inflationRate === p.values.inflationRate;
                return (
                  <Button
                    key={p.label}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePreset(p.values)}
                    className="text-xs"
                  >
                    {p.label}
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2">
            <ShareButtons shareUrl={buildShareUrl(formValues)} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground"
            >
              <RotateCcw className="size-3.5" />
              Reset
            </Button>
          </div>
        </div>
      }
      resultsSection={
        <div className="space-y-4">
          <ResultCard
            icon={PiggyBank}
            metrics={[
              {
                label: "Balance at retirement",
                value: formatCurrency(result.balanceAtRetirement),
                highlight: true,
              },
              {
                label: "Monthly income (4% rule)",
                value: formatCurrency(result.monthlyWithdrawal),
                tooltip:
                  "A common guideline: withdraw 4% of your nest egg per year (about 0.33% per month) for sustainable retirement income.",
              },
              {
                label: "Total contributions",
                value: formatCurrency(result.totalContributions),
              },
              {
                label: "Investment growth",
                value: formatCurrency(result.totalGrowth),
              },
              {
                label: "Years to retirement",
                value: `${result.yearsToRetirement} years`,
              },
            ]}
          />
          {milestones.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Balance at key ages
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[280px] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-2 font-medium text-foreground">Age</th>
                      <th className="pb-2 font-medium text-foreground">Balance</th>
                      <th className="pb-2 font-medium text-muted-foreground">4% / month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {milestones.map((m) => (
                      <tr key={m.age} className="border-b border-border last:border-0">
                        <td className="py-2">
                          <span className="font-medium text-foreground">{m.age}</span>
                          {m.age === 55 && (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <button
                                    type="button"
                                    className="ml-1.5 inline-flex cursor-help items-center text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    aria-label="What is FIRE?"
                                  >
                                    (FIRE)
                                    <Info className="ml-0.5 size-3" aria-hidden />
                                  </button>
                                }
                              />
                              <TooltipContent side="top" className="max-w-[260px]">
                                Financial Independence, Retire Early. Rule of 55 allows penalty-free 401(k) withdrawals from your current employer&apos;s plan if you leave that job in the year you turn 55.
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {m.age === 62 && (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <button
                                    type="button"
                                    className="ml-1.5 inline-flex cursor-help items-center text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    aria-label="What is Early SS?"
                                  >
                                    (Early SS)
                                    <Info className="ml-0.5 size-3" aria-hidden />
                                  </button>
                                }
                              />
                              <TooltipContent side="top" className="max-w-[260px]">
                                Earliest age to claim Social Security. Benefits are reduced if you claim before your full retirement age. Your FRA: {fra} (based on birth year ~{birthYear}).
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {m.age === 67 && (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <button
                                    type="button"
                                    className="ml-1.5 inline-flex cursor-help items-center text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    aria-label="What is FRA?"
                                  >
                                    (FRA)
                                    <Info className="ml-0.5 size-3" aria-hidden />
                                  </button>
                                }
                              />
                              <TooltipContent side="top" className="max-w-[260px]">
                                Full retirement age. Your FRA is {fra} based on birth year ~{birthYear}. Standard Social Security benefit with no reduction at this age.
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {m.age === 70 && (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <button
                                    type="button"
                                    className="ml-1.5 inline-flex cursor-help items-center text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    aria-label="What is Max SS?"
                                  >
                                    (Max SS)
                                    <Info className="ml-0.5 size-3" aria-hidden />
                                  </button>
                                }
                              />
                              <TooltipContent side="top" className="max-w-[260px]">
                                Maximum Social Security benefits. Delayed retirement credits stop after 70. Best to claim by 70.
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                        <td className="py-2 font-medium tabular-nums">
                          {formatCurrency(m.balance)}
                        </td>
                        <td className="py-2 tabular-nums text-muted-foreground">
                          {formatCurrency(m.monthly)}/mo
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {(() => {
            const pieData = [
              { name: "Initial savings", value: formValues.currentSavings, color: PIE_COLORS.savings },
              { name: "Contributions", value: result.totalContributions, color: PIE_COLORS.contributions },
              { name: "Growth", value: result.totalGrowth, color: PIE_COLORS.growth },
            ].filter((d) => d.value > 0);
            const showPie = result.balanceAtRetirement > 0 && pieData.length >= 2;
            if (!showPie) return null;
            return (
              <div className="rounded-lg border border-border bg-card p-5">
                <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Breakdown
                </p>
                {mounted ? (
                  <PieChartWrapper data={pieData} height={220} />
                ) : (
                  <div className="h-[220px] w-full animate-pulse rounded-lg bg-muted" aria-hidden />
                )}
              </div>
            );
          })()}
        </div>
      }
      chartSection={
        mounted ? (
          <AreaChartWrapper
            data={chartData}
            dataKeys={[
              { key: "balance", color: CHART_COLOR, name: "Retirement balance" },
            ]}
          />
        ) : (
          <div className="h-[300px] w-full animate-pulse rounded-lg bg-muted" />
        )
      }
      explanationSection={
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              How it&apos;s calculated
            </h2>
            <p className="mb-3 text-muted-foreground">
              Your balance grows using compound interest: current savings plus
              monthly contributions, earning your expected annual return
              (compounded monthly). The formula is the same as compound interest
              with regular contributions. Inflation is used to show your
              &quot;real&quot; purchasing power—what your withdrawal would feel like
              in today&apos;s dollars.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Example calculation
            </h2>
            <p className="mb-3 text-muted-foreground">
              Age 35, retire at 65, $50,000 saved, $500/month contribution, 7%
              expected return:
            </p>
            <ExplanationTable
              headers={["Input", "Value"]}
              rows={[
                ["Current savings", "$50,000"],
                ["Monthly contribution", "$500"],
                ["Expected return", "7% (compounded monthly)"],
                ["Years to retirement", "30"],
                ["Balance at 65", "≈ $730,000"],
                ["4% annual withdrawal", "≈ $29,200/year (~$2,433/month)"],
              ]}
            />
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              The 4% rule
            </h2>
            <p className="text-muted-foreground">
              The 4% rule suggests you can withdraw 4% of your retirement savings
              annually without running out of money over a 30-year retirement.
              This calculator shows your projected monthly income at that rate.
              Actual needs depend on your lifestyle, longevity, and market
              conditions—treat it as a guideline, not a guarantee.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Limitations
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Assumes a fixed return; real markets fluctuate.</li>
              <li>Does not include Social Security, pensions, or taxes.</li>
              <li>4% rule is based on historical data; future results may differ.</li>
              <li>Inflation and longevity risk are simplified.</li>
            </ul>
          </div>
        </div>
      }
    />
  );
}
